import React from 'react'
import PropTypes from 'prop-types'

/**
 * This is a basic user-friendly error card component
 * Designed to accept a title and message to display to the user passed in at the component level
 * 
 * Currently the only action available is to go back to the previous page via the browser history,
 * but we could extend this to accept an array of actions to display as buttons or links.
 * 
 * Like in our WaitOn component, this could be used where we pass in the refetch function to be displayed next to or instead of the go back button.
 * 
 * @param {string} title - Larger text at the top of the card
 * @param {string} message - Smaller text below the title
 */
const ErrorCard = ({ title, message, actionComponent: ActionComponent = null }) => {
  return (
    <div className="flex flex-col items-center w-full max-w-lg gap-8 p-6 h-screen sm:h-fit bg-neutral-pri shadow-md rounded-lg my-20 mx-auto">
      {/* Icon Section */}
      <div className="relative w-full flex items-center flex-row justify-center mt-4 xs:mt-10">
        <img src="/img/pf404.svg" className='w-1/2 min-w-[200px]' alt="404" />
        <div
          className={`talk-bubble w-1/2 max-w-sm h-fit -mt-[5%] -ml-[15%] sm:-ml-[13%] text-sm rounded-lg px-4 py-2 shadow-lg`}
        >
          <p className='text-base font-bold text-neutral-pri'>{title}</p>
          <p className="text-sm text-neutral-pri whitespace-pre-line">{message}</p>
        </div>
      </div>

      {/* Contact Section */}
      <p className="text-center text-neutral-sec">
        Contact your system administrator. If problems persist, reach out to <a href="mailto:prosupport@simulations-plus.com" className="text-blue-500">prosupport@simulations-plus.com</a> for assistance.
      </p>
      {/* Action Section */}
      {ActionComponent ? (
        <ActionComponent />
      ) : (
        <div className="w-full">
          <button
            onClick={() => window.history.back()}
            className="btn w-full m-0 flex gap-2 flex-row items-center justify-center"
          >
            <i className="fa fa-arrow-left" />
            Go back
          </button>
        </div>
      )}
    </div>
  )
}

ErrorCard.propTypes = {
  title: PropTypes.string.isRequired
  , message: PropTypes.string.isRequired
  , actionComponent: PropTypes.elementType // Optional component to render as an action, defaults to a "Go back" button
}

export default ErrorCard;